import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  Checkbox,
  FormControlLabel,
  Grid,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { RefreshOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import * as yup from "yup";
import { useAuthContext } from "../../../context";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../shared/hooks";
import { ToastContent } from "../../../shared/components";
import imageSite from "../../../assets/fundo_login.png";
import { AxiosError } from "axios";


const loginSchema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup.string().required().min(5),
});

type CaptchaChallenge = {
  prompt: string;
  answer: string;
  fieldLabel: string;
  fieldPlaceholder: string;
};

type LoginSecurityStorage = {
  failedAttempts: number;
  updatedAt: number;
};

const LOGIN_SECURITY_STORAGE_KEY = "garantti_login_security";
const CAPTCHA_LOCK_WINDOW_MS = 15 * 60 * 1000;

const readStoredLoginSecurity = (): LoginSecurityStorage | null => {
  const storedValue = window.localStorage.getItem(LOGIN_SECURITY_STORAGE_KEY);

  if (!storedValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(storedValue) as Partial<LoginSecurityStorage>;

    if (
      typeof parsedValue.failedAttempts !== "number" ||
      typeof parsedValue.updatedAt !== "number"
    ) {
      window.localStorage.removeItem(LOGIN_SECURITY_STORAGE_KEY);
      return null;
    }

    if (Date.now() - parsedValue.updatedAt > CAPTCHA_LOCK_WINDOW_MS) {
      window.localStorage.removeItem(LOGIN_SECURITY_STORAGE_KEY);
      return null;
    }

    return {
      failedAttempts: parsedValue.failedAttempts,
      updatedAt: parsedValue.updatedAt,
    };
  } catch {
    window.localStorage.removeItem(LOGIN_SECURITY_STORAGE_KEY);
    return null;
  }
};

const writeStoredLoginSecurity = (failedAttempts: number) => {
  if (failedAttempts <= 0) {
    window.localStorage.removeItem(LOGIN_SECURITY_STORAGE_KEY);
    return;
  }

  const payload: LoginSecurityStorage = {
    failedAttempts,
    updatedAt: Date.now(),
  };

  window.localStorage.setItem(LOGIN_SECURITY_STORAGE_KEY, JSON.stringify(payload));
};

const createCaptchaChallenge = (): CaptchaChallenge => {
  const challengeType = Math.floor(Math.random() * 5);

  switch (challengeType) {
    case 0: {
      const left = Math.floor(Math.random() * 8) + 2;
      const right = Math.floor(Math.random() * 8) + 1;

      return {
        prompt: `${left} + ${right} = ?`,
        answer: String(left + right),
        fieldLabel: "Resultado do captcha",
        fieldPlaceholder: "Digite o resultado",
      };
    }
    case 1: {
      const left = Math.floor(Math.random() * 10) + 6;
      const right = Math.floor(Math.random() * (left - 1)) + 1;

      return {
        prompt: `${left} - ${right} = ?`,
        answer: String(left - right),
        fieldLabel: "Resultado do captcha",
        fieldPlaceholder: "Digite o resultado",
      };
    }
    case 2: {
      const left = Math.floor(Math.random() * 4) + 2;
      const right = Math.floor(Math.random() * 5) + 2;

      return {
        prompt: `${left} x ${right} = ?`,
        answer: String(left * right),
        fieldLabel: "Resultado do captcha",
        fieldPlaceholder: "Digite o resultado",
      };
    }
    case 3: {
      const values = Array.from({ length: 3 }, () => Math.floor(Math.random() * 20) + 1);

      return {
        prompt: `Qual e o maior numero: ${values.join(", ")}?`,
        answer: String(Math.max(...values)),
        fieldLabel: "Maior numero",
        fieldPlaceholder: "Digite o maior numero",
      };
    }
    default: {
      const start = Math.floor(Math.random() * 8) + 2;
      const step = Math.floor(Math.random() * 4) + 2;
      const values = [start, start + step, start + step * 2];

      return {
        prompt: `Complete a sequencia: ${values.join(", ")}, ...`,
        answer: String(start + step * 3),
        fieldLabel: "Proximo numero",
        fieldPlaceholder: "Digite o proximo numero",
      };
    }
  }
};

export const LoginPresentation: React.FC = () => {
  const { login } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(
    () => readStoredLoginSecurity()?.failedAttempts ?? 0,
  );
  const [captchaChallenge, setCaptchaChallenge] = useState<CaptchaChallenge>(createCaptchaChallenge);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [honeypotValue, setHoneypotValue] = useState("");
  const navigate = useNavigate();
  const toastHandler = useToast();

  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const captchaRequired = failedAttempts >= 3;

  useEffect(() => {
    writeStoredLoginSecurity(failedAttempts);
  }, [failedAttempts]);

  const regenerateCaptcha = () => {
    setCaptchaChallenge(createCaptchaChallenge());
    setCaptchaAnswer("");
    setCaptchaError("");
  };

  const handleSubmit = async () => {
    setEmailError("");
    setPasswordError("");
    setCaptchaError("");
    setIsLoading(true);

    try {
      await loginSchema.validate({ email, password }, { abortEarly: false });

      if (captchaRequired) {
        if (honeypotValue.trim()) {
          setCaptchaError("Validacao de seguranca invalida.");
          regenerateCaptcha();
          setIsLoading(false);
          return;
        }

        if (captchaAnswer.trim() !== captchaChallenge.answer) {
          setCaptchaError("Confirme o captcha corretamente.");
          regenerateCaptcha();
          setIsLoading(false);
          return;
        }
      }

      try {
        await login(email, password);
        setFailedAttempts(0);
        setCaptchaAnswer("");
        setHoneypotValue("");
        window.localStorage.removeItem(LOGIN_SECURITY_STORAGE_KEY);
        navigate("/");
      } catch (e) {
        const messageTitle = "Erro no login";
        let messageContent =
          "Verifique a senha e o login digitado. Caso persista, entre em contato conosco.";

        if (e instanceof AxiosError && e.response?.status == 403) {

          if (e.response?.data?.message) {
            messageContent = e.response?.data?.message;
          }
        }

        toastHandler.present({
          type: "error",
          position: "top-right",
          messageOrContent: (
            <ToastContent title={messageTitle} content={messageContent} />
          ),
        });

        setFailedAttempts((currentValue) => {
          const nextValue = currentValue + 1;

          if (nextValue >= 3) {
            regenerateCaptcha();
          }

          return nextValue;
        });
      } finally {
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);

      if (e instanceof yup.ValidationError) {
        e.inner.forEach((error) => {
          if (error.path === "email") {
            setEmailError(error.message);
          } else if (error.path === "password") {
            setPasswordError(error.message);
          }
        });
      }
    }
  };

  const handleForgotPassword = () => {
    setIsLoading(true);

    navigate("/forgot-password");
  };

  return (
      <Grid container height={"100vh"}>
        <Box
          zIndex={1}
          position={"absolute"}
          width={"100vw"}
          height={"100vh"}
          sx={{
            backgroundImage: `url(${imageSite})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box
            sx={{ width: "100%", height: "100%" }}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Card
              sx={{
                borderRadius: "8px",
                maxWidth: "500px",
                minHeight: "550px",
                height: "auto",
                marginLeft: smDown ? "" : "20%",
              }}
            >
              <CardContent
                sx={{ margin: "1rem 2rem", maxWidth: "400px", minHeight: "300px" }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={1}
                  width={400}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Box
                    width="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={"15px"}
                  >
                    <img
                      src="logo_garantti_login.png"
                      alt="Logo"
                      height={"80px"}
                    />
                  </Box>
                  <Typography variant="h6" align="center" color="GrayText">
                    Login de usuário
                  </Typography>
                  <Typography variant="caption" align="center">
                    Insira seu e-mail e sua senha abaixo para concluir seu login.
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    value={email}
                    disabled={isLoading}
                    error={!!emailError}
                    helperText={emailError}
                    onKeyDown={() => setEmailError("")}
                    onChange={(e) => setEmail(e.target.value)}
                    size={"small"}
                    sx={{ margin: "1rem 0" }}
                  />
                  <TextField
                    fullWidth
                    label="Senha"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    disabled={isLoading}
                    error={!!passwordError}
                    helperText={passwordError}
                    onKeyDown={() => setPasswordError("")}
                    onChange={(e) => setPassword(e.target.value)}
                    size={"small"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onClick={() => setShowPassword((currentValue) => !currentValue)}
                            disabled={isLoading}
                            aria-label={showPassword ? "Ocultar senha" : "Exibir senha"}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {captchaRequired && (
                    <Box
                      width="100%"
                      display="flex"
                      flexDirection="column"
                      gap={1.25}
                      mt={1.5}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e6ebf2",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                        Detectamos multiplas tentativas de login. Confirme o captcha para continuar.
                      </Typography>
                      <TextField
                        tabIndex={-1}
                        autoComplete="off"
                        label="Deixe este campo vazio"
                        value={honeypotValue}
                        onChange={(e) => setHoneypotValue(e.target.value)}
                        sx={{ display: "none" }}
                      />
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            px: 1.5,
                            py: 1,
                            borderRadius: 1.5,
                            backgroundColor: "#ffffff",
                            border: "1px dashed #cfd8e3",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" display="block">
                            Desafio de seguranca
                          </Typography>
                          <Typography variant="body1" fontWeight={700}>
                            {captchaChallenge.prompt}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={regenerateCaptcha} disabled={isLoading} aria-label="Gerar novo captcha">
                          <RefreshOutlined fontSize="small" />
                        </IconButton>
                      </Box>
                      <TextField
                        fullWidth
                        label={captchaChallenge.fieldLabel}
                        placeholder={captchaChallenge.fieldPlaceholder}
                        value={captchaAnswer}
                        disabled={isLoading}
                        error={!!captchaError}
                        helperText={captchaError}
                        onChange={(e) => setCaptchaAnswer(e.target.value.trim())}
                        size="small"
                      />
                    </Box>
                  )}
                </Box>
              </CardContent>
              <CardActions sx={{ margin: "1rem 2rem", maxWidth: "400px" }}>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  width="100%"
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                  >
                    <FormControlLabel
                      control={<Checkbox name="lembre=me" />}
                      label="Lembre-me"
                    />
                    <Button
                      variant="text"
                      disabled={isLoading}
                      onClick={handleForgotPassword}
                    >
                      Esqueceu sua senha ?
                    </Button>
                  </Box>
                  <Button
                    variant="contained"
                    disabled={isLoading}
                    onClick={handleSubmit}
                    endIcon={
                      isLoading ? (
                        <CircularProgress variant="indeterminate" />
                      ) : undefined
                    }
                    sx={{
                      width: "inherit",
                      background: "#7924c7",
                      margin: "1rem 0",
                    }}
                  >
                    Entrar
                  </Button>
                </Box>
              </CardActions>
            </Card>
          </Box>
        </Box>
      </Grid>
  );
};
